<template>
  <div class="print-settlement-main">
    <div class="left-container">
      <div class="d-flex align-items-center gap-3">
        <Dropdown
          placeholder="Report Type"
          :items="reportTypes"
          itemKey="name"
          :selectedObject.sync="selectedReportType"
          :errorMessage.sync="selectedReportTypeError"
          :isRequired="true"
        />
        <Button
          class="mt-1 ml-auto flex-shrink-0"
          style="width: fit-content"
          :disabled="previewDisabled"
          @click="previewClicked"
          >Preview</Button
        >
      </div>
      <div class="table-container">
        <TableBox
          title="Payroll List"
          :tableData.sync="payrollData"
          :dataType="payrollTableDataType"
          :selectedRow.sync="selectedPayrolls"
          :hideColumns="['Indicator', 'Remarks', 'OverTime', 'PayMonth', 'PayYear']"
          :maxSelection="-1"
          :loading="payrollLoading"
          :showSelectAll="true"
        />
      </div>
    </div>
    <div class="right-container" style="justify-content: center">
      <DesignerV3
        v-if="showReportPreview"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        :downloadName="downloadFileName"
        ref="designerV3"
      />
      <div v-else class="print-placeholder"> Select a record and click on <strong>Preview</strong> to get started </div>
    </div>
  </div>
</template>

<script>
export default {
  computed: {
    previewDisabled() {
      return this.selectedPayrolls.length == 0;
    },
  },
  data: function () {
    return {
      userDetails: null,
      companyDetails: [],
      departmentList: [],

      payrollData: [],
      selectedPayrolls: [],
      payrollTableDataType: {
        'Nett Pay': 'number',
        Basic: 'number',
        Revenue: 'number',
        Commission: 'number',
        'Special Day': 'number',
        'Extra Inc.': 'number',
        Expenses: 'number',
        'Loan Deduct': 'number',
        Violations: 'number',
        Bonus: 'number',
        SOCSO: 'number',
        EPF: 'number',
        'Employer SOCSO': 'number',
        'Employer EPF': 'number',
        Tabung: 'number',
        'Income Tax': 'number',
        'Unpaid Leave': 'number',
        'Annual Bonus': 'number',
        SIP: 'number',
        'Employer SIP': 'number',
      },
      payrollLoading: false,
      payMonth: '',
      payYear: '',

      reportTypes: [{ name: 'Detailed' }, { name: 'Summary' }],
      selectedReportType: { name: 'Detailed' },
      selectedReportTypeError: '',

      showReportPreview: false,
      downloadFileName: '',
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
        options: {
          fileName: '',
          folder: '',
        },
      },

      seq: 1,
    };
  },
  methods: {
    previewClicked: async function () {
      if (!this.selectedReportType) {
        this.selectedReportTypeError = 'Select a report type.';
        return;
      }

      this.$fullscreenLoading(`Loading Preview (0/${this.selectedPayrolls.length})`);
      this.payMonth = this.selectedPayrolls[0]['PayMonth'];
      this.payYear = this.selectedPayrolls[0]['PayYear'];

      const reportFiles = [];
      const general = {
        companyName: this.userDetails['CoName'],
        printDate: new Date().formatString('yyyy-MM-dd HH:mm:ss'),
        printBy: this.userDetails['LoginUserId'],
      };

      let processedCount = 1;
      for (const p of this.selectedPayrolls) {
        this.seq = 1;
        const dataToPost = {
          payrollno: p['Payroll #'],
          detail: this.selectedReportType['name'] == 'Detailed' ? '1' : '0',
          month: this.payMonth,
          year: this.payYear,
        };
        const details = await this.$apiCallV2('/DriverPayroll/PrintDriverSettlement', dataToPost);

        // formatting based on selected type
        this.selectedReportType['name'] == 'Detailed' ?
          this.formatDetailedReport(details)
        : this.formatSummaryReport(details);

        details['payrollNo'] = p['Payroll #'];
        details['totalCommission'] = this.$currencyFormat(details['totalCommission']);
        details['totalSpecialDay'] = this.$currencyFormat(details['totalSpecialDay']);
        details['totalExpenses'] = this.$currencyFormat(details['totalExpenses']);
        details['totalExtraCommission'] = this.$currencyFormat(details['totalExtraCommission']);
        details['totalAdvances'] = this.$currencyFormat(details['totalAdvances']);
        details['totalViolation'] = this.$currencyFormat(details['totalViolation']);
        details['totalUnpaidLeave'] = this.$currencyFormat(details['totalUnpaidLeave']);
        details['NettPay'] = this.$currencyFormat(details['NettPay']);

        details['basicpay'] = this.$currencyFormat(details['basicpay']);
        details['epf'] = this.$currencyFormat(details['epf']);
        details['socso'] = this.$currencyFormat(details['socso']);
        details['sip'] = this.$currencyFormat(details['sip']);
        details['incomeTax'] = this.$currencyFormat(details['incomeTax']);
        details['tabung'] = this.$currencyFormat(details['tabung']);
        details['overTime'] = this.$currencyFormat(details['overTime']);

        // will show an indicator if payroll is reversed
        details['isReversed'] = p['Indicator'] == 'C';

        reportFiles.push(details);
        this.$fullscreenLoading(`Loading Preview (${processedCount++}/${this.selectedPayrolls.length})`);
      }

      const reportName =
        this.selectedReportType['name'] == 'Detailed' ? 'DriverSettlementDetail.json' : 'DriverPayslip.json';
      this.designerV3ReportFiles = Array.from({ length: this.selectedPayrolls.length }, () => reportName);
      this.downloadFileName =
        this.selectedReportType['name'] == 'Detailed' ?
          `DriverSettlement_${new Date().formatString('yyyyMMddHHmm')}`
        : `Payslip_${new Date().formatString('yyyyMMddHHmm')}`;
      this.designerV3ReportData = reportFiles;
      this.designerV3SharedData = general;
      this.showReportPreview = true;
      this.$fullscreenLoading();
    },
    formatDetailedReport: function (details) {
      // format data for detailed driver settlement report
      details['TotalTonnage'] = this.$currencyFormat(details['TotalTonnage']);
      details['TotalM3'] = this.$currencyFormat(details['TotalM3']);
      details['TotalKM'] = this.$currencyFormat(details['TotalKM']);
      details['_commission'] = this.$currencyFormat(
        parseFloat(details['totalCommission']) + parseFloat(details['totalExtraCommission']),
      );

      // order is important for below fields so that the seq # in the report stays linear
      this.formatField(details, 'basicpay', 'BasicPay');

      if (details['commission'].length > 0) {
        details['_showCommission'] = true;
        details['_seqCommission'] = this.seq++;
        details['commission'].forEach((e) => {
          e['jiDate'] = new Date(e['jiDate']).formatString('dd/MM/yy');
          e['amount'] = this.$currencyFormat(e['amount']);
        });
      }

      if (details['extracommission'].length > 0) {
        details['_showExtraCommission'] = true;
        details['_seqExtraCommission'] = this.seq++;
        details['extracommission'].forEach((e) => {
          e['jiDate'] = new Date(e['jiDate']).formatString('dd/MM/yy');
          e['amount'] = this.$currencyFormat(e['amount']);
        });
      }

      if (details['expenses'].length > 0) {
        details['_showExpenses'] = true;
        details['_seqExpenses'] = this.seq++;
        details['expenses'].forEach((e) => {
          e['expDate'] = new Date(e['expDate']).formatString('dd/MM/yyyy');
          e['expAmt'] = this.$currencyFormat(e['expAmt']);
        });
      }

      this.formatField(details, 'socso', 'SOCSO');
      this.formatField(details, 'epf', 'EPF');
      this.formatField(details, 'sip', 'SIP');
      this.formatField(details, 'incomeTax', 'IncomeTax');

      if (details['advance'].length > 0) {
        details['_showAdvances'] = true;
        details['_seqAdvances'] = this.seq++;
        details['advance'].forEach((e) => {
          e['advDate'] = new Date(e['advDate']).formatString('dd/MM/yyyy');
          e['advAmt'] = this.$currencyFormat(e['advAmt']);
        });
      }

      this.formatField(details, 'bonusInc', 'BonusInc');

      if (details['violation'].length > 0) {
        details['_showViolation'] = true;
        details['_seqViolation'] = this.seq++;
        details['violation'].forEach((e) => {
          e['vDate'] = new Date(e['vDate']).formatString('dd/MM/yyyy');
          e['vAmt'] = this.$currencyFormat(e['vAmt']);
          e['vBalance'] = this.$currencyFormat(e['vBalance']);
        });
      }

      if (details['special'].length > 0) {
        details['_showSpecialInc'] = true;
        details['_seqSpecialInc'] = this.seq++;
        details['special'].forEach((e) => {
          e['jiDate'] = new Date(e['jiDate']).formatString('dd/MM/yy');
          e['amount'] = this.$currencyFormat(e['amount']);
        });
      }

      this.formatField(details, 'tabung', 'Tabung');

      if (details['unpaidleave'].length > 0) {
        details['_showUnpaidLeave'] = true;
        details['_seqUnpaidLeave'] = this.seq++;
        details['unpaidleave'].forEach((e) => {
          e['leaveStart'] = new Date(e['leaveStart']).formatString('dd/MM/yyyy');
          e['leaveEnd'] = new Date(e['leaveEnd']).formatString('dd/MM/yyyy');
          e['leaveDays'] = this.$currencyFormat(e['leaveDays']);
          e['leaveAmt'] = this.$currencyFormat(e['leaveAmt']);
        });
      }

      this.formatField(details, 'annualbonus', 'AnnualBonus');
      this.formatField(details, 'overTime', 'Overtime');

      // below fields may be hidden in summary report so move formatting here
      details['annualbonus'] = this.$currencyFormat(details['annualbonus']);
      details['bonusInc'] = this.$currencyFormat(details['bonusInc']);
    },
    formatSummaryReport: function (details) {
      // formatting for payslip report
      const dept = this.departmentList.getSelected(details['Staff_Department'], 'ID');
      details['Staff_Department'] = dept['DeptDesc'];

      // format company address
      details['Addr1'] = this.companyDetails['CompanyAddress1'];
      details['Addr2'] = this.companyDetails['CompanyAddress2'];
      details['Addr3'] = this.companyDetails['CompanyAddress3'];
      details['Addr4'] = this.companyDetails['CompanyAddress4'];

      // format pay month
      const monthProcess = new Date(2000, Number(this.payMonth) - 1, 1).toLocaleString('en-MY', { month: 'long' });
      details['_payMonth'] = `${monthProcess} ${this.payYear}`;

      // calculate total deduction
      details['_totalDeductions'] = this.$currencyFormat(
        parseFloat(details['epf']) +
          parseFloat(details['socso']) +
          parseFloat(details['incomeTax']) +
          parseFloat(details['totalAdvances']) +
          parseFloat(details['totalViolation']) +
          parseFloat(details['totalUnpaidLeave']) +
          parseFloat(details['sip']),
      );

      // calculate others section
      details['_totalOther'] = this.$currencyFormat(
        parseFloat(details['totalExpenses']) +
          parseFloat(details['totalExtraCommission']) +
          parseFloat(details['totalSpecialDay']) +
          parseFloat(details['tabung']),
      );

      // calculate total earnings
      details['_totalEarnings'] = this.$currencyFormat(
        parseFloat(details['basicpay']) +
          parseFloat(details['totalCommission']) +
          parseFloat(details['bonusInc']) +
          parseFloat(details['annualbonus']) +
          parseFloat(details['_totalOther']),
      );

      // hide if value is 0
      details['_IncentivesTitle'] = parseFloat(details['bonusInc']) > 0 ? 'Incentives' : '';
      details['bonusInc'] = parseFloat(details['bonusInc']) > 0 ? this.$currencyFormat(details['bonusInc']) : '';

      details['_OthersTitle'] = parseFloat(details['_totalOther']) > 0 ? 'Others' : '';
      details['_totalOther'] =
        parseFloat(details['_totalOther']) > 0 ? this.$currencyFormat(details['_totalOther']) : '';

      details['_AnnualBonusTitle'] = parseFloat(details['annualbonus']) > 0 ? 'Annual Bonus' : '';
      details['annualbonus'] =
        parseFloat(details['annualbonus']) > 0 ? this.$currencyFormat(details['annualbonus']) : '';

      // YTD
      details['CoEPF'] = this.$currencyFormat(details['CoEPF']);
      details['CoSOCSO'] = this.$currencyFormat(details['CoSOCSO']);
      details['YTDCoEPF'] = this.$currencyFormat(details['YTDCoEPF']);
      details['YTDEPF'] = this.$currencyFormat(details['YTDEPF']);
      details['YTDCoSOCSO'] = this.$currencyFormat(details['YTDCoSOCSO']);
      details['YTDSOCSO'] = this.$currencyFormat(details['YTDSOCSO']);
      details['YTDBasic'] = this.$currencyFormat(details['YTDBasic']);
      details['YTDIncTax'] = this.$currencyFormat(details['YTDIncTax']);
    },
    formatField: function (details, key, suffix) {
      // simplify formatting for non-array fields (only for detailed report)
      if (details[key] != '0') {
        details[`_show${suffix}`] = true;
        details[`_seq${suffix}`] = this.seq++;
      }
    },
    async sasControlFunction() {
      this.$fullscreenLoading('Checking Security Access');
      const sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=HMS&page=Driver Batch Payroll`);
      if (sasControl.find((k) => k['Control'] == 'MnuPrintPayslip')) {
        // redirect to home page if no access
        await this.$showAlert('error', 'Access Denied', 'You do not have permission to access this page.');
        this.$router.push(`/Home/${this.$route.params['user']}`);
      }
    },
  },
  async mounted() {
    await this.sasControlFunction();
    this.$fullscreenLoading('Getting Details');
    this.userDetails = await this.$apiCallV2(`/Login/GetDetails`);
    this.departmentList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'DEPARTMENT' });
    this.companyDetails = (
      await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.userDetails['CoCode'],
      })
    )['Company'];

    if (this.$route.query['processid']) {
      const dataToPost = {
        monthprocessid: this.$route.query['processid'],
        getallbatches: '0',
      };
      this.payrollData = await this.$apiCallV2('/DriverPayroll/GetBatchPayrollListing', dataToPost);
    }
    this.$fullscreenLoading();
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-settlement-main {
  font-size: 12px;
  padding: 10px 45px;
  width: 100%;
  height: 100%;
  display: flex;
  gap: 6px;

  > .left-container,
  .right-container {
    height: 100%;
    width: 100%;
    padding: 5px;
    border-radius: 5px;
    overflow: hidden;
    background-color: white;
    display: flex;
    flex-direction: column;
    gap: 6px;

    > .table-container {
      overflow: auto;
      height: 100%;
    }

    > .print-placeholder {
      text-align: center;
      font-size: 1.25rem;
      width: 100%;
    }
  }
}
</style>
